import { Button } from "@coucou/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { downloadFromApi } from "../lib/download";
import { shiftDayKey } from "../lib/time";
import { SALON_ID, SALON_TIMEZONE as TZ, api } from "../lib/trpc";
import { startOfWeek } from "./planning-views";

/**
 * Les semaines proposees a l'export, dans l'ordre du menu.
 *
 * `depuis` est toujours un LUNDI : c'est ce que l'API renvoie dans le nom du
 * fichier, et le telechargement doit porter le meme nom des deux cotes.
 */
function choixExport(dateKey: string, libellePremiere: string) {
  const lundi = startOfWeek(dateKey);
  const suivant = shiftDayKey(lundi, 7);
  return [
    {
      cle: "courante",
      libelle: `${libellePremiere} (${libelleSemaine(lundi)})`,
      depuis: lundi,
      nombre: 1 as const,
    },
    {
      cle: "suivante",
      libelle: `Semaine suivante (${libelleSemaine(suivant)})`,
      depuis: suivant,
      nombre: 1 as const,
    },
    { cle: "deux", libelle: "Les deux semaines", depuis: lundi, nombre: 2 as const },
  ];
}

/** « 14 au 20 sept. » — le mois une seule fois quand la semaine n'en change pas. */
function libelleSemaine(lundi: string): string {
  const dimanche = shiftDayKey(lundi, 6);
  const complet = new Intl.DateTimeFormat("fr-FR", { timeZone: TZ, day: "numeric", month: "short" });
  const jourSeul = new Intl.DateTimeFormat("fr-FR", { timeZone: TZ, day: "numeric" });
  const memeMois = lundi.slice(0, 7) === dimanche.slice(0, 7);
  const format = memeMois ? jourSeul : complet;
  return `${format.format(new Date(`${lundi}T12:00:00Z`))} au ${complet.format(new Date(`${dimanche}T12:00:00Z`))}`;
}

/**
 * Export PDF du planning, une semaine ou deux — et depot dans les documents.
 *
 * Partage entre l'ecran Planning et Reglages > Horaires : c'est depuis ce
 * dernier qu'on cale les semaines, et devoir changer d'ecran pour imprimer ce
 * qu'on vient de saisir etait un aller-retour de trop.
 *
 * Trois choix plutot qu'un bouton unique. Deux semaines restent le cas
 * courant — l'affiche de la salle de pause —, mais la semaine d'apres n'est
 * pas toujours arbitree, et l'imprimer reviendrait a diffuser une trame qu'on
 * va corriger. Le menu permet aussi de n'imprimer QUE la semaine suivante,
 * celle qu'on vient de finir de caler, sans reimprimer celle qui est deja
 * affichee au mur.
 *
 * Le DEPOT range le meme PDF dans les documents de chaque employee qui figure
 * au planning. Republier une periode remplace sa version precedente, et toute
 * modification d'horaires ou d'absence regenere les plannings deja deposes :
 * l'API s'en charge, l'ecran n'a rien a surveiller.
 */
export function ExportPlanning({
  dateKey,
  libellePremiere = "Semaine affichée",
}: {
  dateKey: string;
  /** « Semaine affichée » depuis le planning ; « Première semaine » depuis les horaires. */
  libellePremiere?: string;
}) {
  const queryClient = useQueryClient();
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [ouvert, setOuvert] = useState(false);
  const boite = useRef<HTMLDivElement>(null);

  // Referme au clic exterieur et a Echap : un menu qu'on ne peut fermer qu'en
  // choisissant force la main.
  useEffect(() => {
    if (!ouvert) return;
    const auPointeur = (event: PointerEvent) => {
      if (!boite.current?.contains(event.target as Node)) setOuvert(false);
    };
    const auClavier = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOuvert(false);
    };
    document.addEventListener("pointerdown", auPointeur);
    document.addEventListener("keydown", auClavier);
    return () => {
      document.removeEventListener("pointerdown", auPointeur);
      document.removeEventListener("keydown", auClavier);
    };
  }, [ouvert]);

  // Le message de reussite s'efface seul : il confirme, il n'a rien a demander.
  useEffect(() => {
    if (!succes) return;
    const minuterie = setTimeout(() => setSucces(null), 4000);
    return () => clearTimeout(minuterie);
  }, [succes]);

  const exporter = async (depuis: string, nombre: 1 | 2) => {
    setOuvert(false);
    setErreur(null);
    setSucces(null);
    setEnCours(true);
    try {
      // Le meme nom que celui pose par l'API, `depuis` etant deja un lundi.
      await downloadFromApi(
        `/planning.pdf?from=${depuis}&semaines=${nombre}`,
        nombre === 1 ? `planning-semaine-${depuis}.pdf` : `planning-2-semaines-${depuis}.pdf`,
      );
    } catch (error) {
      setErreur(error instanceof Error ? error.message : "Export impossible.");
    } finally {
      setEnCours(false);
    }
  };

  const deposer = useMutation({
    mutationFn: (choix: { depuis: string; nombre: 1 | 2 }) =>
      api.admin.documents.publishPlanning.mutate({
        salonId: SALON_ID,
        monday: choix.depuis,
        weeks: choix.nombre,
      }),
    onMutate: () => {
      setOuvert(false);
      setErreur(null);
      setSucces(null);
    },
    onSuccess: (resultat) => {
      setSucces(
        resultat.deposes === 0
          ? "Personne ne travaille sur cette période : aucun document déposé."
          : `Planning déposé dans les documents de ${resultat.deposes} employée${resultat.deposes > 1 ? "s" : ""}.`,
      );
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["mes-documents"] });
    },
    onError: (error) => setErreur(error.message),
  });

  const choix = choixExport(dateKey, libellePremiere);
  const classeEntree =
    "block w-full px-4 py-2.5 text-left text-sm text-encre-800 transition-colors hover:bg-creme-100";

  return (
    <div ref={boite} className="relative">
      <Button
        size="sm"
        variant="secondary"
        loading={enCours || deposer.isPending}
        aria-haspopup="menu"
        aria-expanded={ouvert}
        onClick={() => setOuvert((etat) => !etat)}
      >
        Planning PDF
        <span aria-hidden className="text-[10px] text-encre-600">
          ▼
        </span>
      </Button>

      {ouvert && (
        <div
          role="menu"
          aria-label="Semaines à exporter"
          className="absolute right-0 top-11 z-20 w-80 overflow-hidden rounded-xl border border-creme-300 bg-white py-1 shadow-soft"
        >
          <p className="px-4 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-encre-400">
            Télécharger
          </p>
          {choix.map((entree) => (
            <button
              key={entree.cle}
              type="button"
              role="menuitem"
              onClick={() => exporter(entree.depuis, entree.nombre)}
              className={classeEntree}
            >
              {entree.libelle}
            </button>
          ))}

          <div className="my-1 border-t border-creme-200" />
          <p className="px-4 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-encre-400">
            Déposer dans les documents de l'équipe
          </p>
          {choix.map((entree) => (
            <button
              key={entree.cle}
              type="button"
              role="menuitem"
              onClick={() => deposer.mutate({ depuis: entree.depuis, nombre: entree.nombre })}
              className={classeEntree}
            >
              {entree.libelle}
            </button>
          ))}
          <p className="px-4 pb-2 pt-1 text-[11px] leading-snug text-encre-400">
            Remplace la version déjà déposée pour la même période ; mise à jour automatique à
            chaque modification d'horaires.
          </p>
        </div>
      )}

      {erreur && (
        <p
          role="alert"
          className="absolute right-0 top-11 z-20 w-64 rounded-lg bg-grenat-100 px-3 py-2 text-xs text-grenat-700 shadow-soft"
        >
          {erreur}
        </p>
      )}

      {succes && (
        <p
          role="status"
          className="absolute right-0 top-11 z-20 w-64 rounded-lg bg-sauge-100 px-3 py-2 text-xs text-sauge-700 shadow-soft"
        >
          {succes}
        </p>
      )}
    </div>
  );
}
